/**
 * Geospatial utilities for delivery radius validation
 */

const EARTH_RADIUS_KM = 6371;

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate the distance between two geographic coordinates using the Haversine formula.
 * @param lat1 - Latitude of point 1 (degrees)
 * @param lng1 - Longitude of point 1 (degrees)
 * @param lat2 - Latitude of point 2 (degrees)
 * @param lng2 - Longitude of point 2 (degrees)
 * @returns Distance in kilometers
 */
export function calculateDistanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
}

/**
 * Check if a delivery location is within the allowed radius of the restaurant.
 * @param restaurantLat - Restaurant latitude
 * @param restaurantLng - Restaurant longitude
 * @param deliveryLat - Delivery location latitude
 * @param deliveryLng - Delivery location longitude
 * @param maxRadiusKm - Maximum allowed delivery radius in km
 * @returns Object with isWithinRadius boolean and the calculated distance
 */
export function checkDeliveryRadius(
  restaurantLat: number,
  restaurantLng: number,
  deliveryLat: number,
  deliveryLng: number,
  maxRadiusKm: number,
): { isWithinRadius: boolean; distanceKm: number } {
  const distanceKm = calculateDistanceKm(
    restaurantLat,
    restaurantLng,
    deliveryLat,
    deliveryLng,
  );

  return {
    isWithinRadius: distanceKm <= maxRadiusKm,
    distanceKm: Math.round(distanceKm * 10) / 10, // Round to 1 decimal
  };
}

